import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DesktopShoppingMenuDropdown from '../DesktopShoppingMenuDropdown';
import useBodega from '../../utils/hooks/bodegaHook';
import { desktopCategory, StyledNav } from './styles';
import useMenuData from '../../utils/hooks/cmsDataHook';
import { MENU_TYPES, DATA_TYPES, BODEGA_DEFAULTS } from '../../constants';
import { AppContext } from '../../context/appContext';

const MAX_COLUMNS = 5;

/**
 * Renders list of shopping categories with dropdowns that contain links to subcategories
 * @param {object} param
 * @param {object} param.topBarRef - Reference to the top bar element needed to add insurance that
 * menus will actually close when the cursor leaves. When the cursor moves too fast we might miss the
 * mouseleave event
 * @param {object} param.topBarRef.current
 * @returns jsx
 */
const DesktopShoppingMenu = ({ topBarRef }) => {
  const desktopMainMenu = useMenuData(DATA_TYPES.HEADER, MENU_TYPES.MAIN_MENU);
  const { MAIN_NAVIGATION } = BODEGA_DEFAULTS;
  const mainNavigationLabel = useBodega('nav_header_main_navigation', MAIN_NAVIGATION);
  const [openMenuCount, setOpenMenuCount] = useState(0);
  const [, actions] = useContext(AppContext);

  useEffect(() => {
    actions?.setIsScrimOpen(!!openMenuCount);
  }, [openMenuCount]);

  // used to add a listener that closes all the shopping menus on mouseleave
  const containerRef = React.useRef(null);
  if (!desktopMainMenu) {
    return null;
  }

  const getChildMenu = mainMenuLink => {
    return mainMenuLink?.children?.slice(0, MAX_COLUMNS);
  };

  return (
    <StyledNav
      aria-label={mainNavigationLabel}
      data-testid="desktop-menu-container"
      ref={containerRef}
    >
      <ul className={desktopCategory} role="menu">
        {desktopMainMenu.children.map((data, mainMenuIndex) => {
          const mainMenuLink = data?.label ? data : data?.children?.[0];
          if (!mainMenuLink) return null;
          if (mainMenuLink.flags.hideFromDesktop) return null;
          const desktopColumns = getChildMenu(mainMenuLink);
          const hasChildMenu = mainMenuLink?.children?.length > 0;
          // if the main menu is not a link and if it also does not have any children then ignore it.
          // This is not an imaginary scenario as it already happened with US data
          if (!hasChildMenu && !mainMenuLink?.url) {
            return;
          }
          return (
            <li key={mainMenuIndex}>
              <DesktopShoppingMenuDropdown
                containerRef={containerRef}
                data-testid="desktop-dropdown-menu-container"
                desktopColumns={desktopColumns}
                hasChildMenu={hasChildMenu}
                mainMenuLink={mainMenuLink}
                setOpenMenuCount={setOpenMenuCount}
                topBarRef={topBarRef}
              />
            </li>
          );
        })}
      </ul>
    </StyledNav>
  );
};

DesktopShoppingMenu.propTypes = {
  topBarRef: PropTypes.object,
  showDesktopContent: PropTypes.bool,
};

export default DesktopShoppingMenu;
